if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.laptopMql = window.matchMedia('(min-width: 990px) and (max-width: 1599px)');
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.addEventListener('click', (event) => {
          const button = event.target.closest('button');
          const thumbnail = button?.closest('[data-target]');
          if (thumbnail) this.setActiveMedia(thumbnail.dataset.target, false);
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.classList.add('rd-has-photo-arrows');
        this.arrowControls = this.querySelector('.rd-gallery-arrows');
        this.arrowControls?.querySelectorAll('button').forEach((button) => {
          button.onclick = () => this.stepMedia(Number(button.dataset.rdMediaStep));
        });
        this.swipeController?.abort();
        this.swipeController = new AbortController();
        // Track the actual mobile scroll position, also when a hidden native
        // slider control or a variant app prevents Dawn's page event firing.
        this.mobileScrollHandler = () => {
          clearTimeout(this.mobileScrollTimer);
          this.mobileScrollTimer = setTimeout(() => this.syncMobileSlide(), 100);
        };
        this.elements.viewer.querySelector('.product__media-list')?.addEventListener(
          'scroll', this.mobileScrollHandler, { passive: true, signal: this.swipeController.signal }
        );
        this.swipeViewportHandler = () => {
          this.updateSwipeArrows(this.elements.viewer.querySelector('.is-active'));
          this.prepareLaptopPhotos();
        };
        this.mql.addEventListener('change', this.swipeViewportHandler, { signal: this.swipeController.signal });
        this.laptopMql.addEventListener('change', this.swipeViewportHandler, { signal: this.swipeController.signal });
        this.updateSwipeArrows(this.elements.viewer.querySelector('.is-active'));
        this.updateArrowVisibility();
        // Prepare the selected clip and at most one upcoming clip.
        this.preloadObserver = new IntersectionObserver(entries => {
          this.galleryNear = entries.some(entry => entry.isIntersecting);
          if (!this.galleryNear || document.hidden || this.dataset.rdPreload === 'false') return;
          const active = this.elements.viewer.querySelector('.is-active');
          active?.querySelector('rd-product-video')?.prepare();
          this.prepareAdjacentVideos(active);
        }, { rootMargin: '300px 0px', threshold: 0 });
        this.preloadObserver.observe(this.elements.viewer);
        this.arrowObserver = new MutationObserver(() => this.updateArrowVisibility());
        const mediaList = this.elements.viewer?.querySelector('.product__media-list');
        if (mediaList) this.arrowObserver.observe(mediaList, { childList: true });
        if (this.dataset.rdColorMedia !== 'true' && this.dataset.rdSense !== 'true' && !this.querySelector('rd-product-video')) return;
        this.autoplayFrame = requestAnimationFrame(() => {
          const firstMedia = this.elements.viewer?.querySelector('.is-active');
          if (firstMedia && (firstMedia.dataset.mediaType === 'image' || firstMedia.querySelector('rd-product-video'))) {
            this.playActiveMedia(firstMedia);
          }
          if (!firstMedia?.querySelector('deferred-media')) return;
          // Wait until the first video is visible; colour changes create a new gallery.
          this.autoplayObserver = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.autoplayObserver.disconnect();
            if (this.isConnected && firstMedia.classList.contains('is-active')) this.playActiveMedia(firstMedia);
          }, { threshold: 0.1 });
          this.autoplayObserver.observe(firstMedia);
        });
      }

      disconnectedCallback() {
        clearTimeout(this.mobileScrollTimer);
        this.swipeController?.abort();
        cancelAnimationFrame(this.autoplayFrame);
        this.autoplayObserver?.disconnect();
        this.arrowObserver?.disconnect();
        this.preloadObserver?.disconnect();
      }

      navigableMedia() {
        return Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'))
          .filter((item) => !item.hidden && item.style.display !== 'none' && (this.mql.matches || item.getClientRects().length > 0));
      }

      updateArrowVisibility() {
        this.updateSwipeArrows(this.elements.viewer.querySelector('.is-active'));
        this.prepareLaptopPhotos();
      }

      prepareLaptopPhotos() {
        if (!this.laptopMql.matches) return;
        const items = this.navigableMedia();
        const activeIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
        // Hidden desktop slides need explicit loading. Warm only the current
        // slide and its neighbours; thumbnail jumps prepare their own slide.
        const indexes = new Set([activeIndex, (activeIndex - 1 + items.length) % items.length, (activeIndex + 1) % items.length]);
        for (const index of indexes) {
          const item = items[index];
          if (!item || item.dataset.mediaType !== 'image') continue;
          item.querySelectorAll('.product__modal-opener--image img').forEach((image) => {
            image.loading = 'eager';
          });
        }
      }

      stepMedia(direction) {
        const items = this.navigableMedia();
        if (items.length < 2) return;
        let index = items.findIndex((item) => item.classList.contains('is-active'));
        if (!this.mql.matches) {
          const left = items[0].parentElement.getBoundingClientRect().left;
          index = items.reduce((best, item, i) =>
            Math.abs(item.getBoundingClientRect().left - left) < Math.abs(items[best].getBoundingClientRect().left - left) ? i : best, 0);
        }
        const next = items[(Math.max(0, index) + direction + items.length) % items.length];
        this.setActiveMedia(next.dataset.mediaId, false);
      }

      isVideoSlide(slide) {
        return ['video', 'external_video'].includes(slide?.dataset.mediaType);
      }

      updateSwipeArrows(slide) {
        const starterKit = this.dataset.rdStarterKit === 'true';
        if (!this.arrowControls || !slide) return;
        if (this.laptopMql.matches && (this.dataset.rdSense === 'true' || starterKit)) {
          // V49: both photos and videos use the same clickable laptop gutters.
          this.arrowsDismissed = true;
        } else if (starterKit && this.dataset.rdSense !== 'true') {
          this.arrowsDismissed = this.laptopMql.matches && slide.dataset.mediaType === 'image';
        } else if (this.dataset.rdSense === 'true' && this.isVideoSlide(slide)) {
          this.arrowsDismissed = false;
        } else if (this.dataset.rdSense === 'true' && slide.dataset.mediaType === 'image' && (this.galleryInteracted || this.isVideoSlide(this.previousArrowSlide))) {
          this.arrowsDismissed = true;

        }
        this.previousArrowSlide = slide;
        // Laptops keep navigation available on every video. Other viewports
        // retain the first-white-video rule from V48.
        const hideVideoArrows = !this.laptopMql.matches && this.isVideoSlide(slide) && slide.dataset.mediaId !== this.dataset.rdVideoArrowsId;
        this.classList.toggle('rd-video-arrows-hidden', hideVideoArrows);
        this.arrowControls.hidden = hideVideoArrows || this.navigableMedia().length < 2;
        this.arrowControls.classList.toggle('rd-gallery-arrows--dismissed', !!this.arrowsDismissed);
        // On laptops the photo arrows move outside the media and stay usable.
        const hidden = this.arrowControls.hidden || (!!this.arrowsDismissed && !this.laptopMql.matches);
        this.arrowControls.setAttribute('aria-hidden', String(hidden));
        this.arrowControls.querySelectorAll('button').forEach((button) => {
          button.tabIndex = hidden ? -1 : 0;
          if (hidden && button === document.activeElement) button.blur();
        });
      }

      onSlideChanged(event) {
        if (!this.mql.matches) {
          this.mobileScrollHandler();
          return;
        }
        const currentMedia = event.detail.currentElement;
        if (!currentMedia) return;
        if (currentMedia) {
          this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((item) => {
            item.classList.toggle('is-active', item === currentMedia);
          });
          this.playActiveMedia(currentMedia);
        }
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${currentMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateSwipeArrows(currentMedia);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.galleryInteracted = true;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.prepareLaptopPhotos();
        // Start the arrow transition in the interaction frame, before scrolling
        // or video loading. Mobile scroll events are a fallback, not a prerequisite.
        this.updateSwipeArrows(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!activeMedia.isConnected) return;
          if (!this.mql.matches || this.elements.thumbnails) {
            const track = activeMedia.parentElement;
            const left = track.scrollLeft + activeMedia.getBoundingClientRect().left - track.getBoundingClientRect().left;
            track.scrollTo({ left });
            if (!this.mql.matches) this.mobileScrollHandler();
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      syncMobileSlide() {
        if (this.mql.matches || !this.isConnected) return;
        const items = this.navigableMedia();
        if (!items.length) return;
        const trackLeft = items[0].parentElement.getBoundingClientRect().left;
        const currentMedia = items.reduce((nearest, item) =>
          Math.abs(item.getBoundingClientRect().left - trackLeft) <
          Math.abs(nearest.getBoundingClientRect().left - trackLeft) ? item : nearest
        );
        const changed = this.mobileActiveMedia !== currentMedia;
        if (currentMedia !== this.previousArrowSlide) this.galleryInteracted = true;
        this.mobileActiveMedia = currentMedia;
        items.forEach((item) => item.classList.toggle('is-active', item === currentMedia));
        this.updateSwipeArrows(currentMedia);
        this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${currentMedia.dataset.mediaId}"]`));
        if (changed) this.playActiveMedia(currentMedia);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          if (!this.isConnected || !activeItem.classList.contains('is-active') || !image.naturalWidth) return;
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete) announce();
        else image.addEventListener('load', announce, { once: true, signal: this.swipeController?.signal });
      }

      prepareAdjacentVideos(activeItem) {
        if (!this.galleryNear || document.hidden || this.dataset.rdPreload === 'false' || navigator.connection?.saveData) return;
        const items = this.navigableMedia();
        const index = items.indexOf(activeItem);
        if (index < 0 || items.length < 2) return;
        const upcoming = items.slice(index + 1).concat(items.slice(0, index));
        upcoming.find(item => item.querySelector('rd-product-video'))?.querySelector('rd-product-video').prepare();
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;
        const changed = this.activePlaybackItem !== activeItem;
        if (changed && this.activePlaybackItem) {
          this.activePlaybackItem.querySelectorAll('video').forEach(video => video.pause());
          this.activePlaybackItem.querySelectorAll('.js-youtube').forEach(frame => frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*'));
          this.activePlaybackItem.querySelectorAll('.js-vimeo').forEach(frame => frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*'));
        }
        this.activePlaybackItem = activeItem;
        this.elements.viewer.querySelectorAll('rd-product-video').forEach(player => player.sync());
        this.prepareAdjacentVideos(activeItem);
        if (activeItem.querySelector('rd-product-video')) return;
        // Repeated slide/scroll events must not pause and restart the same video.
        if (!changed) return;
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        if (!deferredMedia || (this.dataset.rdColorMedia !== 'true' && this.dataset.rdSense !== 'true')) return;
        const video = deferredMedia.querySelector('video');
        if (video) {
          video.muted = true;
          video.playsInline = true;
          video.controls = false;
          video.play()?.catch(() => {});
        }
        const frame = deferredMedia.querySelector('iframe');
        if (frame?.classList.contains('js-youtube')) {
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
        } else if (frame?.classList.contains('js-vimeo')) {
          frame.contentWindow?.postMessage(JSON.stringify({ method: 'play' }), '*');
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
